import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import OnboardingModal from './OnboardingModal';
import useCheckOnboarding from '../../api/useCheckOnboarding';
import './onboarding.css';

export default function OnboardingProvider({ children, isAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { needsOnboarding, hasWarehouses, hasVehicles, isLoading, refetch } =
    useCheckOnboarding();

  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');
  const [lastWarehouseCheck, setLastWarehouseCheck] = useState(false);
  const [lastVehicleCheck, setLastVehicleCheck] = useState(false);

  // Get onboarding state from localStorage
  const getOnboardingState = () => {
    const state = localStorage.getItem('onboardingState');
    return state || 'initial'; // initial, warehouses, vehicles, completed
  };

  // Set onboarding state in localStorage
  const setOnboardingState = (state) => {
    localStorage.setItem('onboardingState', state);
  };

  // Check if we're on a page that should trigger onboarding actions
  const isOnWarehousesPage = location.pathname === '/warehouses';
  const isOnVehiclesPage = location.pathname === '/vehicles';

  // Refetch data when navigating back to onboarding pages
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    const onboardingState = getOnboardingState();
    
    // Refetch when we're on an onboarding page
    if (
      (onboardingState === 'warehouses' && isOnWarehousesPage) ||
      (onboardingState === 'vehicles' && isOnVehiclesPage)
    ) {
      refetch();
    }
  }, [isAuthenticated, isLoading, isOnWarehousesPage, isOnVehiclesPage, refetch]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    const onboardingState = getOnboardingState();

    // Show modal if user needs onboarding and hasn't seen it yet
    if (needsOnboarding && onboardingState === 'initial') {
      setShowModal(true);
    }

    // Check for warehouse creation - only trigger once when status changes
    if (
      onboardingState === 'warehouses' &&
      isOnWarehousesPage &&
      hasWarehouses &&
      !lastWarehouseCheck
    ) {
      setLastWarehouseCheck(true);
      // User just created a warehouse
      setBannerMessage(
        '✓ Entrepôt créé avec succès ! Passons maintenant aux véhicules.'
      );
      setShowBanner(true);
      setOnboardingState('vehicles');

      // Redirect to vehicles after a short delay
      setTimeout(() => {
        setShowBanner(false);
        navigate('/vehicles?onboarding=true');
      }, 2500);
    }

    // Check for vehicle creation - only trigger once when status changes
    if (
      onboardingState === 'vehicles' &&
      isOnVehiclesPage &&
      hasVehicles &&
      !lastVehicleCheck
    ) {
      setLastVehicleCheck(true);
      // User just created a vehicle
      setBannerMessage(
        '✓ Configuration terminée ! Vous pouvez maintenant utiliser toutes les fonctionnalités.'
      );
      setShowBanner(true);
      setOnboardingState('completed');
      localStorage.setItem('onboardingCompleted', 'true');

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        setShowBanner(false);
        navigate('/');
      }, 3000);
    }
  }, [
    isAuthenticated,
    isLoading,
    needsOnboarding,
    hasWarehouses,
    hasVehicles,
    location.pathname,
    navigate,
    isOnWarehousesPage,
    isOnVehiclesPage,
    lastWarehouseCheck,
    lastVehicleCheck,
  ]);

  const handleStartOnboarding = () => {
    setShowModal(false);
    setOnboardingState('warehouses');
    navigate('/warehouses?onboarding=true');
  };

  return (
    <>
      {children}
      {isAuthenticated && (
        <>
          <OnboardingModal isOpen={showModal} onStart={handleStartOnboarding} />
          {showBanner && (
            <div className="onboarding-banner">
              <span className="onboarding-banner-icon">
                <FontAwesomeIcon icon={faCheckCircle} />
              </span>
              <span>{bannerMessage}</span>
            </div>
          )}
        </>
      )}
    </>
  );
}

